import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  IconButton,
  Checkbox,
  FormControlLabel,
  Popover,
  TextField,
  InputAdornment,
  Divider,
  Button,
  Collapse,
  Tooltip,
} from "@mui/material";
import {
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { METAL_PURITY_FACTORS } from "../../constants/MetalPurityFactors.js";
import { BACKGROUND_COLORS, BORDER_COLORS, TABLE_COLORS, MUI_COLORS } from "../../style/colors.js";

const excelSerialToDate = (serial) => {
  if (!serial || isNaN(serial)) return null;
  const excelEpoch = new Date('1899-12-30T00:00:00Z');
  const date = new Date(excelEpoch.getTime() + serial * 24 * 60 * 60 * 1000);
  return date;
};

const formatDate = (date) => {
  if (!date) return '';
  return date.toLocaleDateString('es-ES');
};

// Colores por fábrica para las barras de distribución
const FABRIC_COLORS = {
  cordoba: "#4e79a7",
  euroshine: "#e08a3c",
};

// Metadatos visuales por familia de metal
const FAMILY_META = {
  oro: { label: "Oro", color: "#D4AF37", bg: "rgba(212,175,55,0.12)" },
  platino: { label: "Platino", color: "#8792A2", bg: "rgba(135,146,162,0.14)" },
  plata: { label: "Plata", color: "#9AA0A6", bg: "rgba(154,160,166,0.12)" },
};

// Orden lógico de columnas en la tabla de detalle
const COLUMN_ORDER = [
  "orden",
  "1º_Ref",
  "articulo",
  "cliente",
  "metal",
  "gramos",
  "pdte",
  "tipo_orden",
  "fabric",
  "fecha_entrega",
];

const familyOfMetal = (metal) => {
  const m = (metal || "").toLowerCase();
  if (m.includes("platino")) return "platino";
  if (m.includes("plata")) return "plata";
  return "oro";
};

const ktOfMetal = (metal) => {
  const match = (metal || "").match(/(\d+)\s*kt/i);
  return match ? parseInt(match[1], 10) : 999;
};

const fmtGrams = (n) =>
  `${(n || 0).toLocaleString("es-ES", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}g`;

const QUILATAJE_HEAD_CELL = {
  color: "white",
  fontWeight: "bold",
  fontSize: "0.72rem",
  backgroundColor: TABLE_COLORS.DARK,
  padding: "6px 10px",
};

const QUILATAJE_NUM_CELL = {
  textAlign: "right",
  fontSize: "0.72rem",
  fontFamily: "monospace",
  padding: "4px 10px",
  whiteSpace: "nowrap",
};

export default function ComponentExcelFilters({ data = [], title = "Detalles Excel", subtitle = "", onRequiredMetalsChange }) {
  const [filters, setFilters] = useState({});
  const [searchTerms, setSearchTerms] = useState({});
  const [anchorEl, setAnchorEl] = useState({ column: null, element: null });
  const [expanded, setExpanded] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });

  const columns = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    const allKeys = new Set();
    data.forEach(item => {
      Object.keys(item).forEach(key => allKeys.add(key));
    });
    
    return Array.from(allKeys).sort();
  }, [data]);

  const orderedColumns = useMemo(() => {
    const idx = (c) => {
      const i = COLUMN_ORDER.indexOf(c);
      return i === -1 ? COLUMN_ORDER.length : i;
    };
    return [...columns].sort((a, b) => idx(a) - idx(b) || a.localeCompare(b));
  }, [columns]);

  const getUniqueValues = useCallback((columnKey) => {
    const values = new Set();
    data.forEach(item => {
      const value = item[columnKey];
      if (value === null || value === undefined) {
        values.add('null');
      } else if (columnKey === 'fecha_entrega') {
        // Convertir fecha serial a formato legible para el filtro
        const date = excelSerialToDate(value);
        const formattedDate = date ? formatDate(date) : value;
        values.add(formattedDate);
      } else if (value !== '') {
        values.add(String(value));
      }
    });
    return Array.from(values).sort();
  }, [data]);

  const uniqueValuesByColumn = useMemo(() => {
    const result = {};
    columns.forEach(column => {
      result[column] = getUniqueValues(column);
    });
    return result;
  }, [columns, getUniqueValues]);

  useEffect(() => {
    const initialFilters = {};
    const initialSearchTerms = {};
    
    columns.forEach(column => {
      const uniqueValues = uniqueValuesByColumn[column] || [];
      initialFilters[column] = uniqueValues.reduce((acc, value) => {
        acc[value] = true;
        return acc;
      }, {});
      initialSearchTerms[column] = '';
    });
    
    setFilters(initialFilters);
    setSearchTerms(initialSearchTerms);
  }, [columns, uniqueValuesByColumn]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isPopoverClick = event.target.closest('[role="presentation"]');
      const isFilterIconClick = event.target.closest('[data-filter-icon]');
      
      if (!isPopoverClick && !isFilterIconClick) {
        setAnchorEl({ column: null, element: null });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    
      return data.filter(item => {
        // Filtro por rango de fechas (solo si hay fechas seleccionadas)
        if ((dateRange.startDate && dateRange.startDate !== '') || (dateRange.endDate && dateRange.endDate !== '')) {
          const fechaEntrega = item.fecha_entrega;
          if (fechaEntrega) {
            const itemDate = excelSerialToDate(fechaEntrega);
            if (itemDate) {
              const itemDateStr = itemDate.toISOString().split('T')[0]; // YYYY-MM-DD
              
              if (dateRange.startDate && dateRange.startDate !== '' && itemDateStr < dateRange.startDate) {
                return false;
              }
              if (dateRange.endDate && dateRange.endDate !== '' && itemDateStr > dateRange.endDate) {
                return false;
              }
            }
          }
        }
        
        return columns.every(column => {
          const rawValue = item[column];
          let value;
          
          // Para fechas, usar el valor formateado para comparar con los filtros
          if (column === 'fecha_entrega') {
            if (rawValue === null || rawValue === undefined) {
              value = 'null';
            } else {
              const date = excelSerialToDate(rawValue);
              value = date ? formatDate(date) : String(rawValue);
            }
          } else {
            value = rawValue === null || rawValue === undefined ? 'null' : String(rawValue);
          }
          
          const searchTerm = searchTerms[column]?.toLowerCase() || '';
          
          // Filtro por búsqueda
          if (searchTerm && !value.toLowerCase().includes(searchTerm)) {
            return false;
          }
          
          // Filtro por checkboxes
          const columnFilters = filters[column] || {};
          const isValueSelected = columnFilters[value];
          
          return isValueSelected;
        });
      });
  }, [data, filters, searchTerms, columns, dateRange]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  }, [filteredData, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerms, dateRange]);

  const handleFilterChange = useCallback((column, value, checked) => {
    setFilters(prev => ({
      ...prev,
      [column]: {
        ...prev[column],
        [value]: checked
      }
    }));
  }, []);

  const handleSelectAll = useCallback((column, checked) => {
    const uniqueValues = uniqueValuesByColumn[column] || [];
    const newFilters = uniqueValues.reduce((acc, value) => {
      acc[value] = checked;
      return acc;
    }, {});
    
    setFilters(prev => ({
      ...prev,
      [column]: newFilters
    }));
  }, [uniqueValuesByColumn]);

  const handleSearchChange = useCallback((column, value) => {
    setSearchTerms(prev => ({
      ...prev,
      [column]: value
    }));
  }, []);

  const clearAllFilters = useCallback(() => {
    const clearedFilters = {};
    const clearedSearchTerms = {};
    
    columns.forEach(column => {
      const uniqueValues = uniqueValuesByColumn[column] || [];
      clearedFilters[column] = uniqueValues.reduce((acc, value) => {
        acc[value] = true;
        return acc;
      }, {});
      clearedSearchTerms[column] = '';
    });
    
    setFilters(clearedFilters);
    setSearchTerms(clearedSearchTerms);
    setDateRange({ startDate: '', endDate: '' });
  }, [columns, uniqueValuesByColumn]);

  const handleFilterClick = (event, column) => {
    event.preventDefault();
    event.stopPropagation();
    
    setAnchorEl(prev => {
      if (prev.column === column) {
        return { column: null, element: null };
      }
      return { column, element: event.currentTarget };
    });
  };

  const handleFilterClose = () => {
    setAnchorEl({ column: null, element: null });
  };

  const getColumnLabel = (column) => {
    const labelMap = {
      'orden': 'Orden',
      'articulo': 'Artículo',
      '1º_Ref': '1º Ref',
      'pdte': 'Pendiente',
      'metal': 'Metal',
      'gramos': 'Gramos',
      'fabric': 'Fabric',
      'tipo_orden': 'Tipo Orden',
      'cliente': 'Cliente',
      'fecha_entrega': 'Fecha Entrega'
    };
    return labelMap[column] || column;
  };

  const getActiveFiltersCount = useCallback(() => {
    let count = 0;
    columns.forEach(column => {
      const uniqueValues = uniqueValuesByColumn[column] || [];
      const columnFilters = filters[column] || {};
      const selectedCount = uniqueValues.filter(value => columnFilters[value]).length;
      if (selectedCount < uniqueValues.length) {
        count++;
      }
      if (searchTerms[column]) {
        count++;
      }
    });
    return count;
  }, [columns, uniqueValuesByColumn, filters, searchTerms]);

  const activeFiltersCount = getActiveFiltersCount();

  const calculateRequiredMetals = useCallback((filteredData) => {
    if (!filteredData || filteredData.length === 0) {
      return {
        cordoba: { gold: 0, platinum: 0, silver: 0 },
        euroshine: { gold: 0, platinum: 0, silver: 0 },
      };
    }

    let cordobaGold = 0;
    let cordobaPlatinum = 0;
    let cordobaSilver = 0;
    let eurishineGold = 0;
    let eurishinePlatinum = 0;
    let eurishineSilver = 0;

    filteredData.forEach(item => {
      const fabric = item.fabric || '';
      const orden = String(item.orden || '');
      const metal = item.metal || '';
      const gramos = parseFloat(item.gramos || 0);
      const metalLower = metal.toLowerCase();

      let purityFactor = 0;
      if (metalLower.includes('plata')) {
        purityFactor = METAL_PURITY_FACTORS.SILVER;
      } else if (metalLower.includes('18kt') || metalLower.includes('18 kt')) {
        purityFactor = METAL_PURITY_FACTORS.GOLD['18kt'];
      } else if (metalLower.includes('14kt') || metalLower.includes('14 kt')) {
        purityFactor = METAL_PURITY_FACTORS.GOLD['14kt'];
      } else if (metalLower.includes('9kt') || metalLower.includes('9 kt')) {
        purityFactor = METAL_PURITY_FACTORS.GOLD['9kt'];
      } else if (metalLower.includes('19kt') || metalLower.includes('19 kt')) {
        purityFactor = METAL_PURITY_FACTORS.GOLD['19kt'];
      } else if (metalLower.includes('platino')) {
        purityFactor = METAL_PURITY_FACTORS.PLATINUM;
      }

      if (purityFactor === 0) return;

      const fineWeight = gramos * purityFactor;

      if (fabric === 'FJS' && !orden.startsWith('8') && orden.length === 10) {
        if (metalLower.includes('platino')) {
          cordobaPlatinum += fineWeight;
        } else if (metalLower.includes('plata')) {
          cordobaSilver += fineWeight;
        } else {
          cordobaGold += fineWeight;
        }
      } else if (fabric === 'ES') {
        if (metalLower.includes('platino')) {
          eurishinePlatinum += fineWeight;
        } else if (metalLower.includes('plata')) {
          eurishineSilver += fineWeight;
        } else {
          eurishineGold += fineWeight;
        }
      }
    });

    return {
      cordoba: { gold: cordobaGold, platinum: cordobaPlatinum, silver: cordobaSilver },
      euroshine: { gold: eurishineGold, platinum: eurishinePlatinum, silver: eurishineSilver },
    };
  }, []);


  const requiredMetals = useMemo(() => {
    return calculateRequiredMetals(filteredData);
  }, [filteredData, calculateRequiredMetals]);

  // Resumen por quilataje agrupado por familia de metal (oro/platino/plata)
  const quilatajeSummary = useMemo(() => {
    const summary = {};
    (filteredData || []).forEach((item) => {
      const metal = item.metal || "";
      if (!metal) return;
      const fabric = item.fabric || "";
      const gramos = parseFloat(item.gramos || 0);
      if (!summary[metal]) summary[metal] = { cordoba: 0, euroshine: 0 };
      if (fabric === "FJS") summary[metal].cordoba += gramos;
      else if (fabric === "ES") summary[metal].euroshine += gramos;
    });

    const families = { oro: [], platino: [], plata: [] };
    Object.entries(summary).forEach(([metal, d]) => {
      const total = d.cordoba + d.euroshine;
      if (total === 0) return;
      families[familyOfMetal(metal)].push({
        metal,
        cordoba: d.cordoba,
        euroshine: d.euroshine,
        total,
      });
    });
    families.oro.sort((a, b) => ktOfMetal(a.metal) - ktOfMetal(b.metal));
    families.platino.sort((a, b) => b.total - a.total);
    families.plata.sort((a, b) => b.total - a.total);

    const sumFam = (arr) =>
      arr.reduce(
        (s, x) => ({
          cordoba: s.cordoba + x.cordoba,
          euroshine: s.euroshine + x.euroshine,
          total: s.total + x.total,
        }),
        { cordoba: 0, euroshine: 0, total: 0 }
      );

    const all = [...families.oro, ...families.platino, ...families.plata];
    return {
      families,
      subtotals: {
        oro: sumFam(families.oro),
        platino: sumFam(families.platino),
        plata: sumFam(families.plata),
      },
      grand: sumFam(all),
      isEmpty: all.length === 0,
    };
  }, [filteredData]);

  useEffect(() => {
    if (onRequiredMetalsChange) {
      onRequiredMetalsChange(requiredMetals);
    }
  }, [requiredMetals, onRequiredMetalsChange]);

  const DistBar = ({ cordoba, euroshine, height = 8, width = 120 }) => {
    const total = cordoba + euroshine || 1;
    const cPct = (cordoba / total) * 100;
    const ePct = (euroshine / total) * 100;
    return (
      <Tooltip
        arrow
        title={`Córdoba ${cPct.toFixed(0)}% (${fmtGrams(cordoba)}) · Euroshine ${ePct.toFixed(0)}% (${fmtGrams(euroshine)})`}
      >
        <Box
          sx={{
            display: "flex",
            width,
            height,
            borderRadius: 4,
            overflow: "hidden",
            backgroundColor: "#e9ecef",
            mx: "auto",
            cursor: "help",
          }}
        >
          <Box sx={{ width: `${cPct}%`, backgroundColor: FABRIC_COLORS.cordoba }} />
          <Box sx={{ width: `${ePct}%`, backgroundColor: FABRIC_COLORS.euroshine }} />
        </Box>
      </Tooltip>
    );
  };

  const colAlign = (c) => (c === "gramos" || c === "pdte" ? "right" : "left");

  const renderDataCell = (column, row) => {
    const raw = row[column];

    if (column === "gramos") {
      return (
        <Typography component="span" sx={{ fontFamily: "monospace", fontWeight: "bold", color: "success.main", fontSize: "0.75rem" }}>
          {parseFloat(raw || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}g
        </Typography>
      );
    }

    if (column === "metal") {
      if (!raw) return "—";
      const meta = FAMILY_META[familyOfMetal(raw)];
      return (
        <Chip
          label={raw}
          size="small"
          sx={{ height: 18, fontSize: "0.62rem", fontWeight: "bold", backgroundColor: meta.bg, color: "#37474f", border: `1px solid ${meta.color}` }}
        />
      );
    }

    if (column === "fabric") {
      const isCordoba = raw === "FJS";
      const isEuro = raw === "ES";
      const label = isCordoba ? "Córdoba" : isEuro ? "Euroshine" : raw || "—";
      const color = isCordoba ? FABRIC_COLORS.cordoba : isEuro ? FABRIC_COLORS.euroshine : "#9e9e9e";
      return (
        <Chip
          label={label}
          size="small"
          sx={{ height: 18, fontSize: "0.62rem", fontWeight: "bold", backgroundColor: `${color}1f`, color, border: `1px solid ${color}` }}
        />
      );
    }

    if (column === "fecha_entrega") {
      const date = excelSerialToDate(raw);
      return date ? formatDate(date) : raw || "";
    }

    return raw ?? "";
  };

  const renderQuilatajeSummary = () => (
    <Box sx={{ mb: 2, p: 1.5, backgroundColor: BACKGROUND_COLORS.SECONDARY, borderRadius: 1, border: `1px solid ${BORDER_COLORS.PRIMARY}` }}>
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1 }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: "bold", fontSize: "0.8rem", color: "#495057", lineHeight: 1.2 }}>
            Resumen por Quilataje (gramos en bruto)
          </Typography>
          <Typography variant="caption" sx={{ display: "block", fontSize: "0.68rem", color: "text.secondary" }}>
            Gramos pedidos por quilataje y fábrica, tal cual llegan en el Excel (aún sin convertir a fino 999).
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {[["Córdoba", FABRIC_COLORS.cordoba], ["Euroshine", FABRIC_COLORS.euroshine]].map(([label, color]) => (
            <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: "2px", backgroundColor: color }} />
              <Typography variant="caption" sx={{ fontSize: "0.66rem", color: "text.secondary" }}>
                {label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: 340, boxShadow: 1, borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={QUILATAJE_HEAD_CELL}>Metal</TableCell>
              <TableCell sx={{ ...QUILATAJE_HEAD_CELL, textAlign: "right" }}>Córdoba</TableCell>
              <TableCell sx={{ ...QUILATAJE_HEAD_CELL, textAlign: "right" }}>Euroshine</TableCell>
              <TableCell sx={{ ...QUILATAJE_HEAD_CELL, textAlign: "right" }}>Total</TableCell>
              <TableCell sx={{ ...QUILATAJE_HEAD_CELL, textAlign: "center" }}>Distribución</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {quilatajeSummary.isEmpty ? (
              <TableRow>
                <TableCell colSpan={5} sx={{ textAlign: "center", fontStyle: "italic", color: "#666", py: 2 }}>
                  Sin datos filtrados
                </TableCell>
              </TableRow>
            ) : (
              <>
                {["oro", "platino", "plata"].map((fam) => {
                  const items = quilatajeSummary.families[fam];
                  if (!items.length) return null;
                  const meta = FAMILY_META[fam];
                  const sub = quilatajeSummary.subtotals[fam];
                  const multi = items.length > 1;
                  return (
                    <React.Fragment key={fam}>
                      <TableRow sx={{ backgroundColor: meta.bg }}>
                        <TableCell sx={{ borderLeft: `4px solid ${meta.color}`, fontSize: "0.75rem", py: 0.6 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Box sx={{ width: 9, height: 9, borderRadius: "50%", backgroundColor: meta.color }} />
                            <Typography component="span" sx={{ fontWeight: "bold", fontSize: "0.75rem" }}>
                              {meta.label}
                            </Typography>
                            {fam === "plata" && (
                              <Chip label="no cuenta para fino" size="small" sx={{ height: 15, fontSize: "0.55rem", backgroundColor: "#eceff1", color: "#607d8b" }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...QUILATAJE_NUM_CELL, fontWeight: "bold" }}>{fmtGrams(sub.cordoba)}</TableCell>
                        <TableCell sx={{ ...QUILATAJE_NUM_CELL, fontWeight: "bold" }}>{fmtGrams(sub.euroshine)}</TableCell>
                        <TableCell sx={{ ...QUILATAJE_NUM_CELL, fontWeight: "bold" }}>{fmtGrams(sub.total)}</TableCell>
                        <TableCell sx={{ textAlign: "center", py: 0.6 }}>
                          <DistBar cordoba={sub.cordoba} euroshine={sub.euroshine} />
                        </TableCell>
                      </TableRow>
                      {multi &&
                        items.map((it) => (
                          <TableRow key={it.metal} hover>
                            <TableCell sx={{ pl: 4, fontSize: "0.72rem", color: "#555", py: 0.4 }}>{it.metal}</TableCell>
                            <TableCell sx={{ ...QUILATAJE_NUM_CELL, color: "#666" }}>{fmtGrams(it.cordoba)}</TableCell>
                            <TableCell sx={{ ...QUILATAJE_NUM_CELL, color: "#666" }}>{fmtGrams(it.euroshine)}</TableCell>
                            <TableCell sx={QUILATAJE_NUM_CELL}>{fmtGrams(it.total)}</TableCell>
                            <TableCell sx={{ textAlign: "center", py: 0.4 }}>
                              <DistBar cordoba={it.cordoba} euroshine={it.euroshine} height={6} width={100} />
                            </TableCell>
                          </TableRow>
                        ))}
                    </React.Fragment>
                  );
                })}
                <TableRow sx={{ backgroundColor: "#37474f" }}>
                  <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.75rem", py: 0.7 }}>TOTAL</TableCell>
                  <TableCell sx={{ ...QUILATAJE_NUM_CELL, color: "white", fontWeight: "bold" }}>{fmtGrams(quilatajeSummary.grand.cordoba)}</TableCell>
                  <TableCell sx={{ ...QUILATAJE_NUM_CELL, color: "white", fontWeight: "bold" }}>{fmtGrams(quilatajeSummary.grand.euroshine)}</TableCell>
                  <TableCell sx={{ ...QUILATAJE_NUM_CELL, color: "white", fontWeight: "bold" }}>{fmtGrams(quilatajeSummary.grand.total)}</TableCell>
                  <TableCell sx={{ textAlign: "center" }}>
                    <DistBar cordoba={quilatajeSummary.grand.cordoba} euroshine={quilatajeSummary.grand.euroshine} />
                  </TableCell>
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Card sx={{ mb: 2, boxShadow: 3, borderRadius: 2 }}>
      <CardContent sx={{ p: 1.5 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <MaterialCommunityIcons 
            name="table-large" 
            size={16} 
            style={{ marginRight: 8, color: MUI_COLORS.PRIMARY }}
          />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: MUI_COLORS.PRIMARY, fontSize: "0.8rem" }}>
              {title}
            </Typography>
            {subtitle ? (
              <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem" }}>
                {subtitle}
              </Typography>
            ) : null}
          </Box>
          
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            {activeFiltersCount > 0 && (
              <Chip
                label={`${activeFiltersCount} filtros activos`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ fontSize: "0.6rem", height: "20px" }}
              />
            )}
            
            <IconButton
              onClick={() => setExpanded(!expanded)}
              size="small"
              sx={{ color: "primary.main" }}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Resumen por quilataje (rediseñado) */}
        {renderQuilatajeSummary()}

        {/* Resumen, controles de paginación y botón limpiar */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1, flexWrap: "wrap", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
            <Chip
              label={`Total: ${data.length} registros`}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ fontWeight: "bold", fontSize: "0.8rem", height: "20px" }}
            />
            <Chip
              label={`Filtrados: ${filteredData.length} registros`}
              color="secondary"
              variant="outlined"
              size="small"
              sx={{ fontWeight: "bold", fontSize: "0.8rem", height: "20px" }}
            />
            
            {/* Filtro de rango de fechas */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, ml: 1 }}>
              <Typography variant="caption" sx={{ fontSize: "0.8rem", fontWeight: "bold" }}>
                Fecha:
              </Typography>
              <TextField
                type="date"
                size="small"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                sx={{ 
                  '& .MuiInputBase-input': { 
                    fontSize: '0.8rem', 
                    padding: '4px 8px',
                    height: '20px'
                  },
                  '& .MuiInputBase-root': {
                    height: '20px'
                  },
                  width: '130px'
                }}
                InputLabelProps={{ shrink: true }}
              />
              <Typography variant="caption" sx={{ fontSize: "0.8rem" }}>
                a
              </Typography>
              <TextField
                type="date"
                size="small"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                sx={{ 
                  '& .MuiInputBase-input': { 
                    fontSize: '0.8rem', 
                    padding: '4px 8px',
                    height: '20px'
                  },
                  '& .MuiInputBase-root': {
                    height: '20px'
                  },
                  width: '130px'
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            
            <Button
              size="small"
              startIcon={<ClearIcon />}
              onClick={clearAllFilters}
              sx={{ fontSize: "0.9rem", minWidth: "auto", px: 1, ml: 1 }}
            >
              Limpiar Filtros
            </Button>
          </Box>
          
          {/* Controles de paginación */}
          {filteredData.length > itemsPerPage && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                Mostrar:
              </Typography>
              <TextField
                select
                size="small"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                sx={{ minWidth: 80 }}
                SelectProps={{
                  native: true,
                }}
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={200}>200</option>
                <option value={500}>500</option>
                <option value={1000}>1000</option>
              </TextField>
              
              <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                Página {currentPage} de {totalPages}
              </Typography>
              
              <Box sx={{ display: "flex", gap: 0.5 }}>
                <Button
                  size="small"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  sx={{ minWidth: "auto", px: 1 }}
                >
                  ‹
                </Button>
                <Button
                  size="small"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  sx={{ minWidth: "auto", px: 1 }}
                >
                  ›
                </Button>
              </Box>
            </Box>
          )}
        </Box>

        {/* Tabla */}
        <Collapse in={expanded}>
          <TableContainer 
            component={Paper} 
            sx={{ 
              borderRadius: 1,
              border: "1px solid #e0e0e0",
              overflow: "auto",
              maxHeight: 600,
              minHeight: 300
            }}
          >
            <Table size="small" stickyHeader sx={{ '& .MuiTableHead-root': { position: 'sticky', top: 0, zIndex: 1 } }}>
              <TableHead>
                <TableRow>
                  {orderedColumns.map(column => {
                    const uniqueValues = uniqueValuesByColumn[column] || [];
                    const columnFilters = filters[column] || {};
                    const selectedCount = uniqueValues.filter(value => columnFilters[value]).length;
                    const hasActiveFilters = selectedCount < uniqueValues.length || searchTerms[column];
                    
                    return (
                      <TableCell
                        key={column}
                        sx={{
                          backgroundColor: TABLE_COLORS.DARK,
                          fontWeight: "bold",
                          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                          minWidth: 120,
                          fontSize: "0.8rem",
                          padding: "4px 8px",
                          color: "white",
                          position: "relative",
                          textAlign: "center"
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", position: "relative", width: "100%" }}>
                          <Typography sx={{ fontSize: "0.8rem", fontWeight: "bold", textAlign: "center" }}>
                            {getColumnLabel(column)}
                          </Typography>
                          <Box sx={{ position: "absolute", right: 4, display: "flex", alignItems: "center", gap: 0.5 }}>
                            {hasActiveFilters && (
                              <Chip
                                label={selectedCount < uniqueValues.length ? `${selectedCount}/${uniqueValues.length}` : "✓"}
                                size="small"
                                sx={{
                                  height: 14,
                                  fontSize: "0.5rem",
                                  backgroundColor: "white",
                                  color: "#465d64",
                                  minWidth: "auto",
                                  px: 0.3
                                }}
                              />
                            )}
                            <IconButton
                              data-filter-icon
                              size="small"
                              onClick={(e) => handleFilterClick(e, column)}
                              sx={{
                                color: "white",
                                padding: "2px",
                                "&:hover": {
                                  backgroundColor: "rgba(255, 255, 255, 0.1)"
                                }
                              }}
                            >
                              <ExpandMoreIcon sx={{ fontSize: "0.8rem" }} />
                            </IconButton>
                          </Box>
                        </Box>
                        
                        {/* Popover con opciones de filtro */}
                        <Popover
                          open={anchorEl.column === column}
                          anchorEl={anchorEl.element}
                          onClose={handleFilterClose}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                          }}
                          transformOrigin={{
                            vertical: 'top',
                            horizontal: 'center',
                          }}
                          disableRestoreFocus
                          PaperProps={{
                            sx: {
                              minWidth: 220,
                              maxWidth: 260,
                              maxHeight: 300,
                              p: 0.8
                            }
                          }}
                        >
                          <Box sx={{ p: 0.8 }}>
                            {/* Header del popover */}
                            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={selectedCount === uniqueValues.length}
                                    indeterminate={selectedCount > 0 && selectedCount < uniqueValues.length}
                                    onChange={(e) => handleSelectAll(column, e.target.checked)}
                                    size="small"
                                  />
                                }
                                label={
                                  <Typography variant="subtitle2" sx={{ fontWeight: "bold", fontSize: "0.75rem" }}>
                                    {getColumnLabel(column)} ({selectedCount}/{uniqueValues.length})
                                  </Typography>
                                }
                              />
                            </Box>
                            
                            {/* Campo de búsqueda */}
                            <TextField
                              size="small"
                              placeholder={`Buscar en ${getColumnLabel(column)}...`}
                              value={searchTerms[column] || ''}
                              onChange={(e) => handleSearchChange(column, e.target.value)}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    <SearchIcon sx={{ fontSize: 14 }} />
                                  </InputAdornment>
                                ),
                              }}
                              sx={{ mb: 1, width: "100%", fontSize: "0.75rem" }}
                            />
                            
                            <Divider sx={{ mb: 0.8 }} />
                            
                            {/* Lista de checkboxes */}
                            <Box sx={{ maxHeight: 150, overflow: "auto" }}>
                              {(() => {
                                // Columnas que deben mostrar todos los valores
                                const showAllColumns = ['cliente', 'articulo', 'tipo_orden', 'fabric', 'metal', '1º_Ref'];
                                const shouldShowAll = showAllColumns.includes(column);
                                const maxItems = shouldShowAll ? uniqueValues.length : 50;
                                const valuesToShow = uniqueValues.slice(0, maxItems);
                                
                                return (
                                  <>
                                    {valuesToShow.map(value => (
                                      <FormControlLabel
                                        key={value}
                                        control={
                                          <Checkbox
                                            checked={columnFilters[value] || false}
                                            onChange={(e) => handleFilterChange(column, value, e.target.checked)}
                                            size="small"
                                          />
                                        }
                                        label={
                                          <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>
                                            {value}
                                          </Typography>
                                        }
                                        sx={{ display: "block", mb: 0.3, width: "100%" }}
                                      />
                                    ))}
                                    {!shouldShowAll && uniqueValues.length > 50 && (
                                      <Typography variant="caption" sx={{ color: "text.secondary", fontStyle: "italic", display: "block", mt: 0.8, fontSize: "0.6rem" }}>
                                        ... y {uniqueValues.length - 50} más
                                      </Typography>
                                    )}
                                  </>
                                );
                              })()}
                            </Box>
                          </Box>
                        </Popover>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((row, index) => {
                  const globalIndex = (currentPage - 1) * itemsPerPage + index;
                  return (
                    <TableRow
                      key={globalIndex}
                      hover
                      sx={{ backgroundColor: index % 2 === 0 ? "#ffffff" : "#f7f9fa" }}
                    >
                      {orderedColumns.map(column => (
                        <TableCell
                          key={column}
                          align={colAlign(column)}
                          sx={{
                            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                            fontSize: "0.75rem",
                            padding: "3px 10px",
                            whiteSpace: "nowrap",
                            borderBottom: "1px solid #eceff1",
                          }}
                        >
                          {renderDataCell(column, row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </CardContent>
    </Card>
  );
}
